import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import CodeIcon from "@mui/icons-material/Code";
import { useAlert } from "../../../hooks/useAlert";
import { useBackdatePromotionInviteEventMutation } from "../../../redux-toolkit/features/promotions/promotionsApiSlice";
import { showDevTools } from "../../../utils/apiBaseUrl";
import "./DevTools.css";

const EVENT_DETAIL_PATH =
  /\/promotions\/history\/([a-fA-F0-9]{24})(?:\/|$|\?)/;

const inviteIdFromPath = (pathname = "") => {
  const match = pathname.match(EVENT_DETAIL_PATH);
  return match?.[1] || "";
};

const DevTools = () => {
  const location = useLocation();
  const { displayAlert } = useAlert();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const inviteId = inviteIdFromPath(location.pathname);
  const [backdateEvent, { isLoading }] =
    useBackdatePromotionInviteEventMutation();

  useEffect(() => {
    if (!showDevTools || !open) return undefined;

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!showDevTools) return null;

  const handleBackdate = async () => {
    if (!inviteId || isLoading) return;
    try {
      const result = await backdateEvent({ inviteId }).unwrap();
      displayAlert({
        open: true,
        message: result?.message || "Event date set to yesterday",
        type: "success",
        timeout: "2500",
      });
      setOpen(false);
    } catch (err) {
      displayAlert({
        open: true,
        message:
          err?.data?.message ||
          err?.message ||
          "Unable to backdate this event",
        type: "error",
        timeout: "3000",
      });
    }
  };

  return (
    <div className="devtools" ref={rootRef}>
      <button
        type="button"
        className="devtools__fab"
        aria-label="Open dev tools"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <CodeIcon sx={{ color: "#ffffff", fontSize: 26 }} />
      </button>
      {open && (
        <div className="devtools__panel" role="dialog" aria-label="Dev tools">
          <h3 className="devtools__title">Dev Tools</h3>
          <p className="devtools__hint">
            {inviteId
              ? "Set this event's date to one day before today so it counts as passed."
              : "Open a promotion event, then use this to simulate that it has passed."}
          </p>
          <button
            type="button"
            className="devtools__action"
            onClick={handleBackdate}
            disabled={!inviteId || isLoading}
          >
            {isLoading ? "Updating…" : "Set event date to yesterday"}
          </button>
        </div>
      )}
    </div>
  );
};

export default DevTools;
